import { useCallback, useRef } from 'react';

import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { NavigationExperienceId } from '@atlassian/navigation-perf-utils/constant';

import type { NavErrorBoundaryProps } from '../../ui/types';

type UseReportInteractionErrorArgs = Pick<
	NavErrorBoundaryProps,
	'interactionId' | 'ufoInteractionName'
>;

/**
 * Returns a callback that marks the matching UFO Experience as errored using
 * either the modern `ufoInteractionName` or the legacy `interactionId` prop.
 *
 * The underlying `useUFOReportError` callback is stored in a ref so callers
 * can include the returned function in their own `useCallback` deps without
 * causing churn each time React re-renders the boundary.
 */
export function useReportInteractionError({
	interactionId,
	ufoInteractionName,
}: UseReportInteractionErrorArgs): (error: Error) => void {
	const reportError = useUFOReportError();
	const reportErrorRef = useRef(reportError);
	reportErrorRef.current = reportError;

	return useCallback(
		(error: Error) => {
			const name = ufoInteractionName
				? ufoInteractionName
				: interactionId
					? NavigationExperienceId[interactionId].experienceId
					: undefined;

			if (!name) {
				return;
			}

			reportErrorRef.current({
				name,
				errorMessage: error.message,
				errorStack: error.stack,
				errorStatusCode: error instanceof Response ? error?.status : undefined,
			});
		},
		[interactionId, ufoInteractionName],
	);
}
